import React from 'react';
import { Box, Flex, Spinner } from 'pounceV2';

import EnrichedAIResponseV3 from 'Components/AI/EnrichedAIResponseV3';
import AIExplainLogEventsV3 from 'Components/AIExplainLogEvents/AIExplainLogEventsV3';
import { useGenericAISummaryContextV3 } from 'Components/AIExplainLogEvents/GenericAISummaryContextV3';
import useUrlParams from 'Hooks/useUrlParams';
import useFeatureToggle from 'Hooks/useFeatureToggle';
import { AISummaryStreamParams } from 'Components/AIExplainLogEvents/useFetchGenericAiSummaryV3';
import { AISummarizeKind } from 'Generated/schema';
import { useAIPageThreadsV3 } from 'Pages/AI/hooks/useAIPageThreadsV3';
import { useAIPageActionsV3 } from 'Pages/AI/hooks/useAIPageActionsV3';
import { useGetAIInferenceStreamMetadata } from 'Components/AIExplainLogEvents/graphql/getAiInferenceStreamMetadata.generated';
import { isNewStreamId } from 'Pages/AI/constants';
import { AIResponseFeedbackV3, FeatureContext } from 'Components/AI/AIResponseFeedback';
import AISummaryHeaderV3 from './AISummaryHeaderV3';
import AISummaryThreadListV3 from './AISummaryThreadListV3';

type AISummarySidePanelContentProps = {
  kind: AISummarizeKind;
  alias: string;
  onClose: () => void;
  streamCompletedActions?: (aiSummary: string) => React.ReactNode;
  isPrivate: boolean;
};

const AISummarySidePanelContentV3 = ({
  kind,
  alias,
  onClose,
  streamCompletedActions,
  isPrivate,
}: AISummarySidePanelContentProps) => {
  const enableAISummaryFeedback = useFeatureToggle('ENABLE_AI_SUMMARY_FEEDBACK');
  const { isLoading, usage, riskScoreEvidence, initialUserPrompt } = useGenericAISummaryContextV3();
  const {
    urlParams: { aiStream, parentStreamId },
  } = useUrlParams<AISummaryStreamParams>();

  const { streamsMetadata, isLoadingThreads, hasNextPage, sentinelRef } = useAIPageThreadsV3({
    kind,
    alias,
  });

  const [isThreadListOpen, setIsThreadListOpen] = React.useState(false);
  const [pendingSearchQuery, setPendingSearchQuery] = React.useState<string | null>(null);

  const handleResetUI = React.useCallback(() => {
    setIsThreadListOpen(false);
    setPendingSearchQuery(null);
  }, []);

  const streamIdForMetadata = isNewStreamId(aiStream) ? parentStreamId : aiStream;
  const { data: streamMetadata } = useGetAIInferenceStreamMetadata({
    variables: {
      id: streamIdForMetadata || '',
    },
    skip: !streamIdForMetadata || isLoadingThreads,
  });

  const {
    handleDelete: handleDeleteAction,
    handleTogglePrivacy,
    canEdit,
  } = useAIPageActionsV3({
    streamIdForMetadata,
    streamMetadata,
    onResetUI: handleResetUI,
    onClose,
    streamsMetadata: streamsMetadata?.aiInferenceStreamsMetadata?.edges?.map(edge => edge.node),
  });

  const handleHeaderClose = React.useCallback(() => {
    if (isThreadListOpen) {
      setIsThreadListOpen(false);
    } else {
      onClose();
    }
  }, [isThreadListOpen, onClose]);

  const handleDelete = React.useCallback(
    (id: string) => {
      if (id === streamIdForMetadata) {
        handleDeleteAction();
      }
    },
    [streamIdForMetadata, handleDeleteAction],
  );

  const handleSearchRequest = React.useCallback((query: string) => {
    // Set the search query and open the thread list
    setPendingSearchQuery(query);
    setIsThreadListOpen(true);
  }, []);

  return (
    <Box height="100%" position="relative">
      <AISummaryHeaderV3
        loading={isLoadingThreads}
        kind={kind}
        alias={alias}
        onClose={handleHeaderClose}
        isThreadListOpen={isThreadListOpen}
        onTitleClick={() => setIsThreadListOpen(!isThreadListOpen)}
        onDelete={handleDelete}
        streamsMetadata={streamsMetadata?.aiInferenceStreamsMetadata?.edges?.map(edge => edge.node)}
        isPrivate={isPrivate}
        onTogglePrivacy={handleTogglePrivacy}
        canEdit={canEdit}
        isLoading={isLoading}
        initialUserPrompt={initialUserPrompt}
      />
      <Flex
        direction="column"
        px={6}
        pb={6}
        pt={4}
        height={isThreadListOpen ? 'calc(100vh - 80px)' : 'calc(100% - 65px)'}
        overflow="auto"
      >
        <AIExplainLogEventsV3
          disabled={!canEdit}
          renderContent={(aiSummary, aiSummaryError, endContent) => (
            <Box id="event-explanation" mt={4}>
              {/* Loading Spinner - above AI response, aligned top-left */}
              {isLoading && (
                <Box mb={3} display="flex" justifyContent="flex-start" alignItems="flex-start">
                  <Spinner size="small" />
                </Box>
              )}
              {(aiSummary || aiSummaryError) && (
                <Box>
                  <EnrichedAIResponseV3
                    response={aiSummary || aiSummaryError}
                    finished={!isLoading}
                    usage={usage}
                    onSearchRequest={handleSearchRequest}
                    riskScoreEvidence={riskScoreEvidence}
                  />
                  {
                    // We only show the streamCompletedActions if the stream is completed and there is no error
                    aiSummary &&
                      !isLoading &&
                      !aiSummaryError &&
                      streamCompletedActions &&
                      streamCompletedActions(aiSummary)
                  }
                  {/* Show feedback after AI response is complete */}
                  {enableAISummaryFeedback && aiSummary && !isLoading && !aiSummaryError && (
                    <Box mt={3}>
                      <AIResponseFeedbackV3
                        featureContext={FeatureContext.AlertTriage}
                        promptText={initialUserPrompt}
                      />
                    </Box>
                  )}
                </Box>
              )}
              {endContent && <>{endContent}</>}
            </Box>
          )}
        />
      </Flex>
      <AISummaryThreadListV3
        isOpen={isThreadListOpen}
        kind={kind}
        alias={alias}
        onClose={() => {
          setIsThreadListOpen(false);
          setPendingSearchQuery(null); // Clear the pending query when closing
        }}
        streamsMetadata={streamsMetadata?.aiInferenceStreamsMetadata?.edges?.map(edge => edge.node)}
        loading={isLoadingThreads}
        hasNextPage={hasNextPage}
        sentinelRef={sentinelRef}
        initialSearchQuery={pendingSearchQuery}
      />
    </Box>
  );
};

export default React.memo(AISummarySidePanelContentV3);
